import { useSelector } from "react-redux";
import { ShopFilter } from "@b2bportal/air-shopping-api";
import {
  extractMulticitySegmentsFromQueryParams,
  type FlightSearchValues,
  getSearchState,
} from "@b2bportal/core-flights";
import {
  FlightSearchQueryParams,
  type IPassengerCounts,
  type ITripTerminus,
  type MulticityRouteValues,
  SliceStopCountFilter,
} from "@b2bportal/core-types";
import {
  createQueryParams,
  QueryParamType,
  useParseQueryParams,
} from "@b2bportal/core-utilities";
import { toJsDate } from "@hopper-b2b/common-utils";
import { MAX_MULTICITY_ROUTES } from "@hopper-b2b/types";

import { TripCategory } from "@overrides/types";

const req = {
  string: { type: QueryParamType.requiredString },
  date: { type: QueryParamType.requiredDate },
};

const opt = {
  string: { type: QueryParamType.optionalString },
  number: { type: QueryParamType.optionalNumber },
  boolean: { type: QueryParamType.optionalBoolean },
  date: { type: QueryParamType.optionalDate },
};

const baseParams = {
  [FlightSearchQueryParams.TRIP_CATEGORY]: opt.string,
  [FlightSearchQueryParams.ADULT_COUNT]: opt.number,
  [FlightSearchQueryParams.CHILD_COUNT]: opt.number,
  [FlightSearchQueryParams.INFANT_IN_SEAT_COUNT]: opt.number,
  [FlightSearchQueryParams.INFANT_IN_LAP_COUNT]: opt.number,
  [FlightSearchQueryParams.NO_LCC]: opt.boolean,
  [FlightSearchQueryParams.STOPS_OPTION]: opt.string,
};

const singleCityParams = {
  [FlightSearchQueryParams.ORIGIN]: req.string,
  [FlightSearchQueryParams.DESTINATION]: req.string,
  [FlightSearchQueryParams.DEPARTURE_DATE]: req.date,
  [FlightSearchQueryParams.RETURN_DATE]: opt.date,
};

const multicityParams = {
  [FlightSearchQueryParams.ORIGIN0]: req.string,
  [FlightSearchQueryParams.DESTINATION0]: req.string,
  [FlightSearchQueryParams.DEPARTURE_DATE0]: req.date,
  [FlightSearchQueryParams.ORIGIN1]: req.string,
  [FlightSearchQueryParams.DESTINATION1]: req.string,
  [FlightSearchQueryParams.DEPARTURE_DATE1]: req.date,
  [FlightSearchQueryParams.ORIGIN2]: opt.string,
  [FlightSearchQueryParams.DESTINATION2]: opt.string,
  [FlightSearchQueryParams.DEPARTURE_DATE2]: opt.date,
  [FlightSearchQueryParams.ORIGIN3]: opt.string,
  [FlightSearchQueryParams.DESTINATION3]: opt.string,
  [FlightSearchQueryParams.DEPARTURE_DATE3]: opt.date,
  [FlightSearchQueryParams.ORIGIN4]: opt.string,
  [FlightSearchQueryParams.DESTINATION4]: opt.string,
  [FlightSearchQueryParams.DEPARTURE_DATE4]: opt.date,
};

type ParsedBaseParams = Omit<
  ReturnType<typeof useParseQueryParams<typeof baseParams>>,
  FlightSearchQueryParams.TRIP_CATEGORY
>;
type ParsedSingleCityParams = ReturnType<
  typeof useParseQueryParams<typeof singleCityParams>
>;
type ParsedMulticityParams = ReturnType<
  typeof useParseQueryParams<typeof multicityParams>
>;

export type SingleOrRoundTripParsedParams = ParsedBaseParams &
  ParsedSingleCityParams & {
    tripCategory: TripCategory.ONE_WAY | TripCategory.ROUND_TRIP;
  };

export type MulticityParsedParams = ParsedBaseParams &
  ParsedMulticityParams & {
    tripCategory: TripCategory.MULTI_CITY;
  };

export const useGetShopQueryParams = ():
  | SingleOrRoundTripParsedParams
  | MulticityParsedParams => {
  const parsedBaseParams = useParseQueryParams(baseParams);

  const tripCategory = parsedBaseParams[FlightSearchQueryParams.TRIP_CATEGORY];

  const params =
    tripCategory === TripCategory.MULTI_CITY
      ? multicityParams
      : singleCityParams;

  const parsedFlightRouteParams = useParseQueryParams(params);

  if (tripCategory === TripCategory.MULTI_CITY) {
    return {
      ...parsedBaseParams,
      ...(parsedFlightRouteParams as ParsedMulticityParams),
      tripCategory: TripCategory.MULTI_CITY,
    };
  }

  return {
    ...parsedBaseParams,
    ...(parsedFlightRouteParams as ParsedSingleCityParams),
    tripCategory:
      tripCategory === TripCategory.ONE_WAY
        ? TripCategory.ONE_WAY
        : TripCategory.ROUND_TRIP,
  };
};

/**
 * Hook to check if the current search is the same as the one in the URL
 * by comparing the search values in the redux store with the URL search params
 */

export const useIsSameSearch = () => {
  const shopParams = useGetShopQueryParams();
  const {
    tripCategory,
    adultCount,
    childCount,
    infantInSeatCount,
    infantOnLapCount,
    stopsOption,
    noLCC,
  } = shopParams;

  const searchValues = useSelector(getSearchState);
  const activeTripFilter = getTripFilter(stopsOption, noLCC);
  /** TODO: compare the rest of the search values related with passenger selection */
  const isSameBaseSearch =
    searchValues.tripCategory === tripCategory &&
    isSameNumber(adultCount, searchValues.passengerCounts.adultsCount) &&
    isSameNumber(childCount, searchValues.passengerCounts.childrenCount) &&
    isSameNumber(
      infantInSeatCount,
      searchValues.passengerCounts.infantsInSeatCount
    ) &&
    isSameNumber(
      infantOnLapCount,
      searchValues.passengerCounts.infantsOnLapCount
    ) &&
    activeTripFilter === searchValues.tripFilter;

  if (shopParams.tripCategory === TripCategory.MULTI_CITY) {
    const segments = extractMulticitySegmentsFromQueryParams(shopParams);

    const sameLength = segments.length === searchValues.multicityRoutes.length;
    const routesEqual =
      sameLength &&
      segments.every((seg, index) => {
        const storedRoute = searchValues.multicityRoutes[index];
        return (
          isSameLocation(seg.origin, storedRoute?.origin) &&
          isSameLocation(seg.destination, storedRoute?.destination) &&
          isSameDate(seg.departureDate, storedRoute?.departureDate)
        );
      });

    const isSameSearch = isSameBaseSearch && routesEqual;

    return isSameSearch;
  }
  // Single or round trip
  const { origin, destination, departureDate, returnDate } = shopParams;

  const isSameSearch =
    isSameBaseSearch &&
    isSameLocation(origin, searchValues.origin) &&
    isSameLocation(destination, searchValues.destination) &&
    isSameDate(departureDate, searchValues.departureDate) &&
    isSameDate(returnDate, searchValues.returnDate);

  return isSameSearch;
};

const isSameNumber = (param: number | undefined | null, value: number) => {
  const parsedParam = param == null ? 0 : param;
  const valueAsNumber = isNaN(value) ? 0 : value;
  return valueAsNumber === parsedParam;
};

const isSameLocation = (code?: string, location?: ITripTerminus) => {
  return (
    (code == null && location == null) ||
    (code != null && location != null && location.id.code.code === code)
  );
};

const isSameDate = (queryParamDate?: string | null, storeDate?: string) => {
  return (
    (queryParamDate == null && storeDate == null) ||
    (queryParamDate != null &&
      storeDate != null &&
      storeDate === queryParamDate)
  );
};

export const getTripFilter = (
  stopOptions: string | null | undefined,
  noLCC: boolean | null | undefined
) => {
  if (stopOptions === SliceStopCountFilter.NONE && noLCC === true)
    return ShopFilter.NonStopNoLCC;
  else if (stopOptions === SliceStopCountFilter.NONE) return ShopFilter.NonStop;
  else if (noLCC === true) return ShopFilter.NoLCC;
  else return ShopFilter.NoFilter;
};

const MULTICITY_ROUTE_INDEXES = Array.from(
  { length: MAX_MULTICITY_ROUTES },
  (_, index) => index
);

const FUNNEL_SEARCH_PARAMS: string[] = [
  FlightSearchQueryParams.TRIP_CATEGORY,
  FlightSearchQueryParams.ADULT_COUNT,
  FlightSearchQueryParams.CHILD_COUNT,
  FlightSearchQueryParams.INFANT_IN_SEAT_COUNT,
  FlightSearchQueryParams.INFANT_IN_LAP_COUNT,
  FlightSearchQueryParams.ORIGIN,
  FlightSearchQueryParams.DESTINATION,
  FlightSearchQueryParams.DEPARTURE_DATE,
  FlightSearchQueryParams.RETURN_DATE,
  ...MULTICITY_ROUTE_INDEXES.flatMap((index) => [
    `${FlightSearchQueryParams.ORIGIN}${index}`,
    `${FlightSearchQueryParams.DESTINATION}${index}`,
    `${FlightSearchQueryParams.DEPARTURE_DATE}${index}`,
  ]),
];

/**
 * Normalizes a flight shop query string into the search parameters that define
 * a shopping funnel. Filter-only params (stops, noLCC) are excluded so changing
 * them does not rotate the funnel ID. Returns `undefined` when the query does
 * not describe a complete flight search (e.g. an entry surface with no search).
 */
export const getFlightFunnelSearch = (
  search: string | undefined
): Record<string, string> | undefined => {
  if (!search) {
    return undefined;
  }

  const queryParams = new URLSearchParams(search);

  const hasSingleCitySearch =
    queryParams.has(FlightSearchQueryParams.ORIGIN) &&
    queryParams.has(FlightSearchQueryParams.DESTINATION) &&
    queryParams.has(FlightSearchQueryParams.DEPARTURE_DATE);
  const hasMulticitySearch =
    queryParams.has(FlightSearchQueryParams.ORIGIN0) &&
    queryParams.has(FlightSearchQueryParams.DESTINATION0) &&
    queryParams.has(FlightSearchQueryParams.DEPARTURE_DATE0);

  if (!hasSingleCitySearch && !hasMulticitySearch) {
    return undefined;
  }

  return FUNNEL_SEARCH_PARAMS.reduce<Record<string, string>>((acc, key) => {
    const value = queryParams.get(key);
    if (value != null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export type BuildFlightShopQueryArgs =
  | {
      tripCategory: TripCategory.ROUND_TRIP;
      origin: ITripTerminus;
      destination: ITripTerminus;
      passengerCounts?: IPassengerCounts;
      departureDate: Date | string;
      returnDate: Date | string;
      stopsOption?: SliceStopCountFilter;
      noLCC?: boolean;
    }
  | {
      tripCategory: TripCategory.ONE_WAY;
      origin: ITripTerminus;
      destination: ITripTerminus;
      passengerCounts?: IPassengerCounts;
      departureDate: Date | string;
      stopsOption?: SliceStopCountFilter;
      noLCC?: boolean;
    }
  | {
      tripCategory: TripCategory.MULTI_CITY;
      multicityRoutes: MulticityRouteValues[];
      passengerCounts?: IPassengerCounts;
      stopsOption?: SliceStopCountFilter;
      noLCC?: boolean;
    };

const getRoutesQueryParams = (routes: MulticityRouteValues[]) =>
  routes.reduce<Record<string, string | Date>>((acc, route, index) => {
    acc[`${FlightSearchQueryParams.ORIGIN}${index}`] =
      route.origin?.id.code.code;
    acc[`${FlightSearchQueryParams.DESTINATION}${index}`] =
      route.destination?.id.code.code;
    acc[`${FlightSearchQueryParams.DEPARTURE_DATE}${index}`] = toJsDate(
      route.departureDate
    );
    return acc;
  }, {});

export const buildFlightShopQuery = (params: BuildFlightShopQueryArgs) => {
  const { passengerCounts, stopsOption, noLCC, tripCategory } = params;

  const routesQueryParams =
    "multicityRoutes" in params
      ? getRoutesQueryParams(params.multicityRoutes)
      : {
          [FlightSearchQueryParams.ORIGIN]: params.origin.id.code.code,
          [FlightSearchQueryParams.DESTINATION]:
            params.destination.id.code.code,
          [FlightSearchQueryParams.DEPARTURE_DATE]: toJsDate(
            params.departureDate
          ),
        };

  try {
    const flightShopQueryObject = {
      ...routesQueryParams,
      ...(tripCategory === TripCategory.ROUND_TRIP && {
        [FlightSearchQueryParams.RETURN_DATE]: toJsDate(params.returnDate),
      }),
      [FlightSearchQueryParams.TRIP_CATEGORY]: tripCategory,
      [FlightSearchQueryParams.STOPS_OPTION]: stopsOption,
      [FlightSearchQueryParams.ADULT_COUNT]: passengerCounts?.adultsCount,
      [FlightSearchQueryParams.CHILD_COUNT]: passengerCounts?.childrenCount,
      [FlightSearchQueryParams.INFANT_IN_LAP_COUNT]:
        passengerCounts?.infantsOnLapCount,
      [FlightSearchQueryParams.INFANT_IN_SEAT_COUNT]:
        passengerCounts?.infantsInSeatCount,
      [FlightSearchQueryParams.NO_LCC]: noLCC,
    };
    return createQueryParams(flightShopQueryObject).toString();
  } catch (e) {
    console.error(e);
  }
};

export const buildFlightShopValuesFromQueryParams = (
  queryParams: SingleOrRoundTripParsedParams
): Omit<FlightSearchValues, "origin" | "destination" | "multicityRoutes"> => {
  const {
    departureDate,
    returnDate,
    tripCategory,
    adultCount,
    childCount,
    infantInSeatCount,
    infantOnLapCount,
    stopsOption,
    noLCC,
  } = queryParams;

  // Origin and destination are not returned given you can not build a location from a code
  return {
    departureDate,
    returnDate: returnDate ?? undefined,
    tripCategory:
      tripCategory === TripCategory.ONE_WAY
        ? TripCategory.ONE_WAY
        : TripCategory.ROUND_TRIP,
    passengerCounts: {
      adultsCount: adultCount != null ? adultCount : 0,
      childrenCount: childCount != null ? childCount : 0,
      infantsInSeatCount: infantInSeatCount != null ? infantInSeatCount : 0,
      infantsOnLapCount: infantOnLapCount != null ? infantOnLapCount : 0,
    },
    tripFilter: getTripFilter(stopsOption, noLCC),
  };
};

interface MulticityRouteValuesWithCode
  extends Omit<MulticityRouteValues, "origin" | "destination"> {
  origin: string;
  destination: string;
}

export const buildMulticityFlightShopValuesFromQueryParams = (
  queryParams: MulticityParsedParams
): Omit<FlightSearchValues, "origin" | "destination" | "multicityRoutes"> & {
  multicityRoutes: MulticityRouteValuesWithCode[];
} => {
  const multicityRoutes = extractMulticitySegmentsFromQueryParams(queryParams);

  return {
    tripCategory: TripCategory.MULTI_CITY,
    passengerCounts: {
      adultsCount: queryParams.adultCount != null ? queryParams.adultCount : 0,
      childrenCount:
        queryParams.childCount != null ? queryParams.childCount : 0,
      infantsInSeatCount:
        queryParams.infantInSeatCount != null
          ? queryParams.infantInSeatCount
          : 0,
      infantsOnLapCount:
        queryParams.infantOnLapCount != null ? queryParams.infantOnLapCount : 0,
    },
    tripFilter: getTripFilter(queryParams.stopsOption, queryParams.noLCC),
    multicityRoutes,
  };
};
